import Vue from 'vue'

import {mapGetters} from 'vuex'

import {getMunicipalityBoundsCorners} from 'js/lib/planner/search-location'
import TopologyStopsService from 'js/services/api/topology/stops.js'
import GoogleDirectionsAPI from 'js/services/google_directions_api'
import OTPDirectionsAPI from 'js/services/otp_directions_api'

import TimePicker from 'vue2-timepicker'

import template from './_search-directions-form.pug'

const NO_RESULTS_ERROR = 'ZERO_RESULTS'

const GOOGLE = 'google'
const OPENTRIPPLANNER = 'opentripplanner'

export default Vue.extend({
  template: template(),
  components: {
    TimePicker
  },
  props: {
    userPosition: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      routes: null,
      locationField: null,
      searchError: null,
      temporalReference: 'now',
      hourReference: {
        HH: moment().format('HH'),
        mm: moment().format('mm')
      },
      dateReference: moment().startOf('day').toDate(),
      preventTextEvent: false,
      originStopTimeOut: null,
      originStopParcialName: '',
      originStops: [],
      destinationStops: [],
      originStopId: undefined,
      destinationStopId: undefined
    }
  },
  computed: {
    originStopsOptions() {
      return this.stopsOptions(this.originStops)
    },
    destinationStopsOptions() {
      return this.stopsOptions(this.destinationStops)
    },
    searchDisabled() {
      return !this.originStopId || !this.destinationStopId
    },
    origin() {
      return this.originStopId
    },
    destination() {
      return this.destinationStopId
    },
    userPositionString() {
      return `${this.userPosition.lat}, ${this.userPosition.lng}`
    },
    temporalSelectOptions() {
      return [
        {label: this.$t('planner.timeModes.now'), value: 'now', alt:this.$t('planner.timeModes.now')},
        {label: this.$t('planner.timeModes.departure'), value: 'departure' ,alt:this.$t('planner.timeModes.departure')},
        {label: this.$t('planner.timeModes.arrival'), value: 'arrival' ,alt:this.$t('planner.timeModes.arrival')}
      ]
    },
    showTimeDatePicker() {
      return this.temporalReference !== 'now'
    },
    formattedDate() {
      const formattedDate = moment(this.dateReference)
        .add(this.hourReference.HH, 'hours')
        .add(this.hourReference.mm, 'minutes')
        .toDate()

      return formattedDate
    },
    temporalReferenceParams() {
      if (this.showTimeDatePicker) {
        return {
          arrivalTime: this.temporalReference === 'arrival' ? this.formattedDate : null,
          departureTime: this.temporalReference === 'arrival' ? null : this.formattedDate
        }
      }

      return {}
    },
    isMobileView() {
      return this.$store.state.device === 'mobile' || this.$store.state.device === 'tablet'
    },
    municipalityBounds() {
      const municipality = this.$municipality

      const {southWest, northEast} = getMunicipalityBoundsCorners({
        upperLeft: {lat: municipality.upper_left_bound_lat, lng: municipality.upper_left_bound_lng},
        lowerRight: {
          lat: municipality.lower_right_bound_lat,
          lng: municipality.lower_right_bound_lng
        },
        municipalityCenter: municipality.geo
      })

      return new google.maps.LatLngBounds(
        new google.maps.LatLng(southWest.lat, southWest.lng),
        new google.maps.LatLng(northEast.lat, northEast.lng)
      )
    },

    agenciesWhitelistUrls() {
      return this.currentAppConfig.agencies_whitelist
    },

    filteredRoutes() {
      if (this.routes) {
        // Si hay agencias en la lista blanca
        if (!_.isEmpty(this.agenciesWhitelistUrls)) {
          const routesByAgency = []

          // Iteramos cada ruta
          this.routes.forEach(route => {
            // Obtenemos los pasos que tiene el modo TRANSIT (!walking)
            const filteredSteps = _.filter(route.leg.steps, ['travel_mode', 'TRANSIT'])

            // Filtramos las rutas por las que tienen agencias en la lista blanca
            let filteredRoute = _.filter(filteredSteps, step => {
              const stepUrl = step.transit.line.agencies[0].url;
              const stepDomain = this.getBaseDomain(stepUrl);

              return this.agenciesWhitelistUrls.some(whitelistUrl => {
                const whitelistDomain = this.getBaseDomain(whitelistUrl);
                return whitelistDomain && stepDomain === whitelistDomain;
              });
            });

            //  Si TODOS los pasos de la ruta tienen agencias en la lista blanca añadimos la ruta
            if (!_.isEmpty(filteredRoute) && filteredSteps.length === filteredRoute.length) {
              routesByAgency.push(route)
            }
          })

          // Devolvemos las rutas obtenidas
          return routesByAgency
        }

        // Devolvemos las rutas originales para el caso que no haya agencias en la lista blanca
        return this.routes
      }

      return []
    }
  },
  watch: {
    filteredRoutes(newRoutes) {
      if (_.isEmpty(newRoutes)) {
        this.handleError(NO_RESULTS_ERROR)
      } else {
        this.searchError = null
      }

      this.$emit('routes', newRoutes)
    },
    searchError(newError) {
      this.$emit('search-error', newError)
    },
    userPosition(newValue) {
      if (!newValue) {
        this.locationField = null
      }
    }
  },

  methods: {
    filterOriginStops() {
      this.$emit('get-origin-stops', this.originStopParcialName)
    },
    stopsOptions(stops) {
      return stops.map(s => ({
        label: this.stopName(s), value: `${s.coordinates[1]}, ${s.coordinates[0]}`
      }))
    },

    stopName(stop) {
      return `${stop.display_code} | ${stop.name}`
    },
    updateOriginParcialName(name) {
      if (this.preventTextEvent) {
        this.preventTextEvent = false

        return
      }

      clearTimeout(this.originStopTimeOut)

      this.originStopTimeOut = setTimeout(() => {
        this.originStopParcialName = name
        this.filters.originStopId = null
        this.filterOriginStops()
      }, 500)
    },
    async getStops(params) {
      const defaultParams = {
        municipality_id: this.$municipality.id,
        with_active_routes: true,
        order_by: 'display_code',
        order_dir: 'asc',
        per: 50
      }

      const response = await TopologyStopsService.index({...defaultParams, ...params})

      return this.orderStopsByDisplayCode(response)
    },

    async getOriginStops(partialName = '') {
      if (partialName.length > 0) {
        this.originStops = await this.getStops({
          full_name_normalized: partialName
        })

        this.stopsOptions(this.originStops)
      }
    },

    async getDestinationStops(partialName = '') {
      if (partialName.length > 0) {
        this.destinationStops = await this.getStops({
          full_name_normalized: partialName
        })

        // eslint-disable-next-line max-len
        this.destinationStops = this.destinationStops.filter(stop => stop.id !== this.originStops[0].id)

        this.stopsOptions(this.destinationStops)
      }
    },

    orderStopsByDisplayCode(originStops) {
      return originStops.sort((stopA, stopB) => {
        const displayCodeA = parseInt(stopA.display_code)
        const displayCodeB = parseInt(stopB.display_code)

        if (displayCodeA < displayCodeB) { return -1 }
        if (displayCodeA > displayCodeB) { return 1 }

        return 0
      })
    },

    addPlacesChangedListener(modelName, searchBox) {
      searchBox.addListener('place_changed', () => {
        this.setDirection(modelName, searchBox.getPlace())
      })
    },
    setDirection(fieldName, place) {
      this[fieldName] = `${place.name}, ${place.formatted_address}`
    },
    setLocationField(target) {
      if (this.userPosition) {
        this.locationField = target
      }
    },
    toggleMyLocation() {
      if (this.locationField === 'origin') {
        this.locationField = 'destination'
      } else if (this.locationField === 'destination') {
        this.locationField = 'origin'
      }
    },
    async searchRoutes() {
      const {origin, destination, originDirection, destinationDirection} = this
      this.searchError = null
      this.routes = []
      Vue.store.commit('incrementLoading')

      try {
        const motor = this.currentAppConfig.planner_motor
        if (motor === GOOGLE || motor === undefined) {
          const response = await GoogleDirectionsAPI.index(
            {transitOptions: this.temporalReferenceParams, origin, destination}
          )
          this.routes = response.routes
        } else if (motor === OPENTRIPPLANNER) {
          const response = await OTPDirectionsAPI.index(
            {
              transitOptions: this.temporalReferenceParams,
              origin,
              destination,
              administrativeAreaTimeZone: this.$administrativeAreaTimeZone,
              otp_url: this.currentAppConfig.planner_motor_url,
              originDirection,
              destinationDirection
            }
          )
          this.routes = response.routes
        }
      } catch (error) {
        this.handleError(error)
      }

      Vue.store.commit('decreaseLoading')
    },
    handleError(error) {
      this.searchError = this.getSearchError(error)
    },
    getSearchError(error) {
      return {
        title: this.getSearchErrorPart(error, {title: true}),
        message: this.getSearchErrorPart(error, {title: false})
      }
    },
    getSearchErrorPart(error, options) {
      const {getFullErrorKey} = this
      const formattedErrorName = _.camelCase(error)
      const errorKey = getFullErrorKey(formattedErrorName, options)

      return this.$te(errorKey) ? this.$t(errorKey) : this.$t(getFullErrorKey('generic', options))
    },
    getFullErrorKey(error, {title}) {
      const titlePart = title ? 'Title' : ''

      return `planner.errors.${error}${titlePart}`
    },
    toggleStops() {
      const originStopsOptions = this.stopsOptions(this.originStops)[0].label
      this.originStopsOptions = this.stopsOptions(this.destinationStops)[0].label
      this.destinationStopsOptions = originStopsOptions

      // this.toggleMyLocation()
    },
    datepickerObjectConfig() {
      return {
        enableTime: false,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'D, j M',
        disableMobile: true
      }
    },
    toggleOriginSelector(value) {
      // Al cerrar
      if (!value) {
        clearTimeout(this.originStopTimeOut)

        // Y haber filtrado sin seleccionar ninguno
        if (this.originStopParcialName && !this.filters.originStopId) {
          this.originStopParcialName = ''
          this.filterOriginStops()
        }
      }
    },

    getBaseDomain(url) {
      try {
        const parsedUrl = new URL(url);
        // Remove 'www.' if it exists to normalize
        return parsedUrl.hostname.replace(/^www\./, '');
      } catch (e) {
        return null; // URL invalid
      }
    }
  }
})
